/** TanStack Query wrappers for file upload, retry and delete operations. */

import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  FileResponse,
  FileSource,
  FileType,
  FileUploadResponse,
  TaskResponse,
} from "@patent-studio/api-types";
import { ApiError } from "@/services/api-client";
import {
  deleteFile,
  fileViewUrl,
  getIntakeSlots,
  retryFileProcessing,
  uploadFile,
} from "@/services/files";
import { fileUrlKey, INTAKE_SLOTS_KEY, taskDetailKey } from "./query-keys";
import {
  intakeComponents,
  type IntakeComponentSpec,
} from "@/lib/intake-components";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

export function useUploadFile(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.fileUpload,
    mutationFn: ({
      file,
      fileType,
      source,
    }: {
      file: File;
      fileType?: FileType;
      source?: FileSource;
    }) => uploadFile(taskId, file, fileType, undefined, source),
    onSuccess: (data: FileUploadResponse) => {
      // Append the upload response with safe defaults for fields absent
      // in FileUploadResponse. Do NOT invalidate the query here — the
      // SSE stream (``use-file-processing``) already drives the cache
      // through every status transition via ``setQueryData``. Calling
      // ``invalidateQueries`` races the SSE: the REST snapshot is taken
      // when the worker has the file at an earlier status, but the
      // response lands *after* SSE has already advanced the cache to
      // ``indexed``, clobbering the live status back to ``uploaded``.
      // The symptom — consistently on the last uploaded file because
      // its refetch is most likely to land mid-processing — was the
      // upload bubble never flipping to ready in the UI even though
      // the worker had completed.
      const placeholder = asFileRow(data);
      qc.setQueryData<TaskResponse>(taskDetailKey(taskId), (prev) => {
        if (!prev) return prev;
        return { ...prev, files: [...prev.files, placeholder] };
      });
    },
  });
}

/**
 * The refusals that mean this client's copy of the task is out of date.
 *
 * They say different things, one that the file moved on and one that it is not
 * there at all, and neither can be repaired by guessing which. Both are
 * answered the same way, by reading the task again.
 */
const SAYS_THE_CACHE_IS_WRONG = new Set([404, 409]);

/**
 * Ask for one file to be read again.
 *
 * Nothing is invalidated on success: the outcome travels on the file-status
 * stream, and a REST snapshot taken here would land after the stream had
 * already moved the file on, which is the race described in ``useUploadFile``.
 * What the stream does not carry is whether the file still takes a retry, so
 * that one field is written here. It is the server's own answer rather than a
 * guess at its rule: a file it has just accepted has a fresh hand-over behind
 * it, so it is not waiting on a retry until the next read says otherwise.
 *
 * A refusal that says this copy of the task is wrong is the mirror of that,
 * and it is the normal case rather than a rare one, since the stream moves a
 * file on without saying anything about whether it still takes a retry. The
 * task is then re-read rather than patched from a guess at what the file
 * became. Nothing is re-read when the reader itself is unavailable: that says
 * nothing about the file, which is exactly where it was.
 */
export function useRetryFileProcessing(taskId: string, fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.fileRetry,
    mutationFn: () => retryFileProcessing(taskId, fileId),
    onSuccess: () => {
      qc.setQueryData<TaskResponse>(taskDetailKey(taskId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          files: prev.files.map((f) =>
            f.file_id === fileId ? { ...f, can_retry: false } : f,
          ),
        };
      });
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        SAYS_THE_CACHE_IS_WRONG.has(error.status)
      ) {
        void qc.invalidateQueries({ queryKey: taskDetailKey(taskId) });
      }
    },
  });
}

/** Take a file the server has removed off the cached task, without a refetch. */
export function dropFileFromTask(
  qc: QueryClient,
  taskId: string,
  fileId: string,
): void {
  qc.setQueryData<TaskResponse>(taskDetailKey(taskId), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      files: prev.files.filter((f) => f.file_id !== fileId),
    };
  });
}

export function useDeleteFile(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.fileDelete,
    mutationFn: (fileId: string) => deleteFile(taskId, fileId),
    onSuccess: (_data, fileId) => dropFileFromTask(qc, taskId, fileId),
  });
}

/**
 * The upload's answer as a row of the task's file list, for every upload path.
 *
 * The upload echo carries what the server decided and nothing it could not
 * know yet, so the rest is filled the one way: a file just accepted has been
 * read by nothing, stamped with nothing, and is waiting on no retry. The
 * server owns those answers; the next read replaces the row with its own.
 */
export function asFileRow(response: FileUploadResponse): FileResponse {
  return {
    ...response,
    processing_error: null,
    content_hash: null,
    page_count: null,
    title: null,
    publication_number: null,
    unread_pages: null,
    uploaded_at: new Date().toISOString(),
    can_retry: false,
    processed_at: null,
    // A file the attorney just chose was drawn from nothing.
    rendered_from_file_id: null,
  };
}

/**
 * The URL a file is opened at, as options, because the drawer asks for one
 * per document it can turn to and holds them all while it is open.
 *
 * The URL expires, so it is not kept once nothing is looking at it. While it
 * is looked at it is never asked for again, not on focus and not on
 * reconnection: a fresh URL is a different string, and the viewer keyed on it
 * would open the document from nothing and lose the attorney's place. Its key
 * stands outside the task's, so no invalidation of the task reaches it.
 */
export function fileUrlQuery(taskId: string, fileId: string) {
  return queryOptions({
    queryKey: fileUrlKey(taskId, fileId),
    queryFn: () => fileViewUrl(taskId, fileId),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Said when the slots table could not be read, beside the way to read it again. */
export const SLOTS_NOT_READ = "The upload slots could not be read.";

/** The Intake slots as read: the specs once they arrive, or the failed read and how to ask again. */
export interface IntakeSlotsRead {
  specs: readonly IntakeComponentSpec[] | undefined;
  /** Why the table could not be read, or null while it reads or once it has. */
  failure: unknown;
  retry: () => void;
}

/**
 * The Intake slots, built from the table the API serves, or nothing while it
 * is on its way. The table changes only with a deploy, so it is read once. A
 * read that failed is said, with the way to read it again, so a page never
 * waits on a table that is not coming.
 */
export function useIntakeSlots(): IntakeSlotsRead {
  const { data, error, isError, refetch } = useQuery({
    queryKey: INTAKE_SLOTS_KEY,
    queryFn: getIntakeSlots,
    staleTime: Infinity,
    select: intakeComponents,
  });
  return {
    specs: data,
    failure: isError ? error : null,
    retry: () => void refetch(),
  };
}
